// Labels and small shared helpers for Orbit Development.
//
// The enum values are the database's (migration 910) and read like slugs; these are what a
// person should see. Kept in one file so a status never reads as "in-progress" on one screen
// and "In progress" on another.

export const TYPE_LABEL = {
  'bug': 'Bug',
  'feature': 'Feature',
  'from-scratch': 'From scratch',
};

export const STATUS_LABEL = {
  'defined': 'Defined',
  'ready': 'Ready',
  'in-progress': 'In progress',
  'done': 'Done',
  'blocked': 'Blocked',
};

export const MODE_LABEL = {
  'batch': 'Batch',
  'manual': 'Manual',
};

// Only two statuses earn a colour. `done` is the state you scan for, `blocked` is the state
// that needs someone; the middle three are the normal course of things and stay neutral, so
// that a list of ordinary work does not read as a wall of alerts.
export const STATUS_BADGE = {
  'done': 'o-ok',
  'blocked': 'o-err',
};

export const MODE_HELP = {
  'batch': 'Claude Code can run this unattended.',
  'manual': 'Needs an interactive session.',
};

// Attachments. An image is shown; anything else is listed and downloaded, so the two need
// telling apart in three places — hence one predicate rather than three content-type checks.
// The cap is mirrored in backend/src/modules/orbit_development/functions.py; a browser told to
// PUT a 400MB file finds out after the upload starts, which is the wrong time.
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export function isImage(attachment) {
  return !!attachment?.content_type?.startsWith('image/');
}

/** The extension, upper-cased, for the file card's badge. */
export function fileExt(name) {
  const ext = /\.([A-Za-z0-9]{1,6})$/.exec(name || '');
  return ext ? ext[1].toUpperCase() : 'FILE';
}

export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const value = n / (1024 ** i);
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

export function moduleLabel(options, id) {
  return options?.modules?.find(m => m.id === id)?.label || id;
}

export function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Overlaps worth showing: another open task that names the same file.
 *
 * It used to fall silent when the two tasks were in the same chain, on the reasoning that
 * chaining them was the resolution being asked for. Chains are gone, and nothing replaced them
 * as a way to say "these two are handled together" — so every overlap is now shown, and a
 * shared SECTION deliberately does not silence it: a section is where a task is filed, not a
 * promise about what runs when.
 */
export function openOverlaps(task, overlaps) {
  if (!task || !overlaps?.length) return [];
  return overlaps;
}

/**
 * The task, as a prompt someone can paste into Claude Code.
 *
 * The description and the notes go in verbatim, which is the whole reason those fields store
 * markdown rather than HTML: what makes them readable on the task page is already the format
 * this document is written in.
 *
 * `index`/`total` title the task by its position, for the exported queue where the prompts run
 * one after another and "which one is this" is not otherwise answerable. Passed in rather than
 * the exporter rewriting the first line afterwards: string surgery on another function output is
 * how the two quietly stop matching.
 *
 * Execution mode is deliberately absent. It described how a batch runner that was never built
 * would run the task, the field is not editable anywhere in the UI any more, and in a queue
 * being worked interactively a line saying "needs an interactive session" is noise at best.
 */
export function taskAsPrompt(task, options, attachments = [], { index, total } = {}) {
  const heading = index
    ? `# Task ${index} of ${total} — ${task.title}`
    : `# ${task.title}`;
  const lines = [
    heading,
    '',
    `- Type: ${TYPE_LABEL[task.task_type] || task.task_type}`,
    `- Target module: ${moduleLabel(options, task.target_module)}`,
  ];
  if (task.area) lines.push(`- Page / area: ${task.area}`);
  // The section, like the area, is context that places the task.
  if (task.section_name) lines.push(`- Section: ${task.section_name}`);
  if (task.files?.length) {
    lines.push('- Files touched:');
    task.files.forEach(f => lines.push(`  - ${f}`));
  }
  lines.push('', '## Description', '', task.description || '_No description._');
  if (task.dev_notes) {
    lines.push('', '## Development notes', '', task.dev_notes);
  }
  if (attachments.length) {
    lines.push('', '## Attachments', '');
    // Linked when there is a URL to link, which is the export: those SAS URLs are signed for
    // days, so following one later still works. The Copy-as-prompt button has only the
    // page-lifetime URLs, and a link that is broken by the time anyone follows it is worse
    // than a name — so there it stays a name.
    if (attachments.some(a => a.url)) {
      attachments.forEach(a => lines.push(
        `- ${a.file_name || 'attachment'}${a.url ? ` — ${a.url}` : ' — (no link)'}`));
    } else {
      lines.push(`${attachments.length} attached in Orbit Development: `
        + attachments.map(a => a.file_name || 'attachment').join(', '));
    }
  }
  return lines.join('\n');
}
